// @ts-nocheck - Temporary fix for react-icons type compatibility with Chakra UI
import React from 'react';
import {
    VStack,
    HStack,
    Text,
    Box,
    Icon,
    Avatar,
    Divider,
    Button,
    IconButton,
    Heading,
    Portal
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { FiLogOut, FiHome, FiUsers, FiGift, FiBarChart2, FiX, FiActivity, FiShare2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Sidebar.css';

interface SidebarProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectPanel: (panelType: string) => void;
    activePanel: string | null;
    onClosePanel: () => void;
    panelContent?: React.ReactNode;
    panelTitle?: string;
}

/**
 * Sidebar Component - Navigation Menu
 * Opens from left side with gradient background
 */
const Sidebar: React.FC<SidebarProps> = ({
    isOpen,
    onClose,
    onSelectPanel,
    activePanel,
    onClosePanel,
    panelContent,
    panelTitle
}) => {
    const { user, logout, isAdmin } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();  // Call logout first - clears user state
        onClose();
        window.location.href = '/login';
    };


    // Admin menu items
    const menuItems: Array<{
        icon: any;
        label: string;
        onClick: () => void;
    }> = isAdmin ? [
        {
            icon: FiHome,
            label: 'Dashboard',
            onClick: () => {
                navigate('/indra');
                onClose();
            }
        },
        {
            icon: FiUsers,
            label: 'Users',
            onClick: () => {
                navigate('/indra/users');
                onClose();
            }
        },
        {
            icon: FiGift,
            label: 'Invites',
            onClick: () => {
                navigate('/indra/invites');
                onClose();
            }
        },
        {
            icon: FiActivity,
            label: 'Activity',
            onClick: () => {
                navigate('/indra/activity');
                onClose();
            }
        },
        {
            icon: FiShare2,
            label: 'Shared Charts',
            onClick: () => {
                navigate('/indra/charts');
                onClose();
            }
        },
        {
            icon: FiBarChart2,
            label: 'Stats',
            onClick: () => {
                navigate('/indra/stats');
                onClose();
            }
        }
    ] : [];

    return (
        <Portal>
            {/* Overlay */}
            <Box
                className={`sidebar-overlay ${isOpen ? 'open' : ''}`}
                onClick={onClose}
            />

            {/* Sidebar Container */}
            <Box
                className={`sidebar-container ${isOpen ? 'open' : ''}`}
                maxW={activePanel ? "800px" : "300px"}
            >
                <HStack spacing={0} align="stretch" h="100vh">
                    {/* Sidebar Section */}
                    <Box
                        className="sidebar-content"
                        maxW="300px"
                        w="300px"
                        display="flex"
                        flexDirection="column"
                    >
                        {/* Header */}
                        <Box className="sidebar-header" p={6} pb={4}>
                            <HStack justify="space-between" align="center" width="100%">
                                <Text className="sidebar-title">
                                    Bhrigu Hora
                                </Text>
                                <IconButton
                                    aria-label="Close sidebar"
                                    icon={<Icon as={FiX} />}
                                    size="sm"
                                    variant="ghost"
                                    color="white"
                                    _hover={{ bg: 'whiteAlpha.200' }}
                                    onClick={onClose}
                                />
                            </HStack>
                        </Box>

                        <Box className="sidebar-body" flex={1} px={6} pb={6} overflowY="auto">
                            <VStack spacing={4} align="stretch" height="100%">
                                {/* Admin Menu Items */}
                                {menuItems.length > 0 && (
                                    <VStack spacing={2} align="stretch" flex={1}>
                                        <Text
                                            fontSize="xs"
                                            fontWeight="bold"
                                            color="whiteAlpha.800"
                                            textTransform="uppercase"
                                            letterSpacing="wider"
                                            mb={1}
                                        >
                                            Admin Panel
                                        </Text>
                                        {menuItems.map((item, index) => (
                                            <Box
                                                key={index}
                                                className="sidebar-menu-item"
                                                onClick={item.onClick}
                                            >
                                                <HStack spacing={3}>
                                                    <Icon as={item.icon} boxSize={5} />
                                                    <Text>{item.label}</Text>
                                                </HStack>
                                            </Box>
                                        ))}
                                    </VStack>
                                )}

                                {/* User Section at Bottom */}
                                <Box mt="auto">
                                    <Divider mb={4} borderColor="whiteAlpha.300" />

                                    {/* User Info */}
                                    {user && (
                                        <HStack
                                            className="sidebar-user-section"
                                            spacing={3}
                                            p={3}
                                            borderRadius="12px"
                                            bg="whiteAlpha.100"
                                            _hover={{ bg: 'whiteAlpha.200' }}
                                            cursor="pointer"
                                            onClick={() => {
                                                onSelectPanel('profile');
                                            }}
                                            mb={3}
                                        >
                                            <Avatar
                                                size="sm"
                                                name={user.username}
                                                src={user.avatar}
                                                bg="blue.500"
                                                referrerPolicy="no-referrer"
                                            />
                                            <VStack align="start" spacing={0} flex={1}>
                                                <Text
                                                    fontSize="14px"
                                                    fontWeight="600"
                                                    color="white"
                                                    noOfLines={1}
                                                >
                                                    {user.username}
                                                </Text>
                                                <Text
                                                    fontSize="12px"
                                                    color="whiteAlpha.700"
                                                    noOfLines={1}
                                                >
                                                    {user.email}
                                                </Text>
                                            </VStack>
                                        </HStack>
                                    )}

                                    {/* Logout Button - Glass Effect */}
                                    <Button
                                        onClick={handleLogout}
                                        leftIcon={<Icon as={FiLogOut} />}
                                        width="100%"
                                        size="md"
                                        color="white"
                                        bg="whiteAlpha.200"
                                        backdropFilter="blur(10px)"
                                        border="1px solid"
                                        borderColor="whiteAlpha.300"
                                        _hover={{
                                            bg: 'whiteAlpha.300',
                                            borderColor: 'whiteAlpha.400',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                        }}
                                        _active={{
                                            bg: 'whiteAlpha.400',
                                            transform: 'translateY(0)'
                                        }}
                                        transition="all 0.2s"
                                    >
                                        Logout
                                    </Button>
                                </Box>
                            </VStack>
                        </Box>
                    </Box>

                    {/* Content Panel Section - shown beside sidebar when activePanel is set */}
                    {activePanel && panelContent && (
                        <Box
                            w="500px"
                            bg="white"
                            h="100vh"
                            overflowY="auto"
                            boxShadow="2xl"
                        >
                            {/* Panel Header */}
                            <HStack
                                px={6}
                                py={4}
                                borderBottom="1px solid"
                                borderColor="gray.200"
                                bg="white"
                                position="sticky"
                                top="0"
                                zIndex="1"
                                justify="space-between"
                            >
                                <Heading size="md" color="gray.800">
                                    {panelTitle}
                                </Heading>
                                <IconButton
                                    aria-label="Close panel"
                                    icon={<CloseIcon />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={onClosePanel}
                                />
                            </HStack>

                            {/* Panel Content */}
                            <Box p={6}>
                                {panelContent}
                            </Box>
                        </Box>
                    )}
                </HStack>
            </Box>
        </Portal>
    );
};

export default Sidebar;
